import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Algebra II, second half: the function as an object you can move, undo and
 * unroll.
 *
 * Most of these lessons are deliberately one argument told several times.
 * `function-transformations` states the rules; `inverse-functions` is the
 * reflection in y = x; `logarithms` is that reflection applied to the
 * exponential and nothing more; `trig-graphs` is the same rules applied to a
 * wave. A student who notices they are not learning five things has understood
 * the course.
 *
 * Three later additions sit around that spine. `function-operations` comes
 * before inverses because composition is how an inverse is *checked*, not
 * decoration; `exponential-equations` is what logarithms were invented for and
 * the first place they earn their keep; and `piecewise-functions` is the one
 * genuine outsider — a function whose rule depends on its input, and the only
 * place in the course where an endpoint being open or closed changes an
 * answer.
 *
 * The lesson to labour over is `logarithms`. Taught as three laws to memorise
 * it is forgotten within the year. Taught as "the inverse of an exponential",
 * every law becomes an exponent rule read backwards — and exponent rules were
 * derived, not memorised, back in Grade 8.
 */
export const FUNCTIONS_HS_LESSONS: Lesson[] = [
  lesson({
    id: 'function-transformations-01',
    unitId: 'unit-function-shapes',
    conceptId: 'function-transformations',
    title: 'Transformations of Functions',
    summary: 'Shifting, stretching and reflecting any curve at all — one set of rules for every function you will meet.',
    objectives: [
      'Predict the effect of each change to f(x) on its graph',
      'Tell an inside change from an outside change',
      'Write the equation of a transformed curve',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'One rule instead of a hundred',
        blocks: [
          text('You have already met this idea twice without it being named. y = (x − 3)² is the parabola moved right; y = 2ˣ + 1 is the exponential curve moved up.'),
          text('It turns out the rules do not depend on which curve you started with. Learn them once, on f, and they apply to parabolas, cubics, roots, exponentials, logarithms and waves — every function in the rest of this course and the next one.'),
        ],
      },
      intuition: {
        title: 'Inside and outside',
        blocks: [
          text('Every transformation in this lesson fits one form:'),
          math('y = a \\cdot f\\big(b(x - h)\\big) + k'),
          text('The whole subject is the difference between the changes made **outside** f and those made **inside** it.'),
          list([
            '**Outside** acts on the *output*, so it moves the graph vertically. +k lifts it; ×a stretches it away from the x-axis, and a negative a flips it upside down.',
            '**Inside** acts on the *input*, so it moves the graph horizontally — and it does everything backwards.',
          ]),
          warning('f(x − 3) moves the graph three to the *right*, not the left. Ask which input now gives the old output: to get what f used to give at 0, you need x − 3 = 0, so x = 3. Everything has shifted right. Inside changes run opposite to how they read.', 'The minus that moves right'),
          text('The same reversal applies to stretches. f(2x) makes the graph *narrower*, because the input reaches any given value twice as soon.'),
          table(['Change', 'Where', 'Effect'], [
            ['f(x) + k', 'outside', 'up k'],
            ['a·f(x)', 'outside', 'stretched vertically by a'],
            ['−f(x)', 'outside', 'reflected in the x-axis'],
            ['f(x − h)', 'inside', 'right h'],
            ['f(bx)', 'inside', 'squashed horizontally by b'],
            ['f(−x)', 'inside', 'reflected in the y-axis'],
          ]),
          insight('Every row of that table is one sentence: outside is vertical and behaves as written, inside is horizontal and behaves backwards. If you can reconstruct the table from that sentence, there is nothing here to memorise.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              transform: { h: 2, k: -3 },
              showParent: true,
              min: -5,
              max: 6,
              points: [{ x: 2, y: -3, label: '(2, −3)' }],
              label: 'y = (x−2)² − 3',
            },
            'The dashed curve is y = x². Subtracting 2 inside moved it right, and subtracting 3 outside moved it down — the two minus signs behaving quite differently.',
          ),
          tip('Track one point rather than the whole curve. The vertex of y = x² sits at (0, 0); find where that single point has gone and the rest of the curve follows it.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('The graph of y = √x is moved 4 left, stretched vertically by 3, and reflected in the x-axis. Write its equation.'),
          steps([
            { label: 'Start with the parent function.', latex: 'y = \\sqrt{x}' },
            { label: 'Four left is an inside change, and inside runs backwards.', latex: 'y = \\sqrt{x + 4}' },
            { label: 'A vertical stretch of 3 is outside.', latex: 'y = 3\\sqrt{x + 4}' },
            { label: 'A reflection in the x-axis negates the output.', latex: 'y = -3\\sqrt{x + 4}' },
          ]),
          text('Check it with one point. The parent passes through (0, 0); after the shift that point sits at (−4, 0), and multiplying an output of 0 by −3 leaves it at (−4, 0). The corner of the curve is where it should be.'),
          insight('Order matters when a stretch and a shift act in the same direction. 2f(x) + 1 and 2(f(x) + 1) are different curves — the first stretches then lifts, the second lifts then stretches, which lifts by 2 instead of 1.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('function-transformations', 4)] },
      independent: { title: 'On your own', blocks: [practice('function-transformations', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('function-transformations', 3, 'Several transformations at once.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'y = a·f(b(x − h)) + k covers every transformation in this course.',
            'Outside changes are vertical and behave as written.',
            'Inside changes are horizontal and behave backwards.',
            'A negative outside flips vertically; a negative inside flips horizontally.',
            'The rules do not depend on which function f is.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'inverse-functions-01',
    unitId: 'unit-function-shapes',
    conceptId: 'inverse-functions',
    title: 'Inverse Functions',
    summary: 'Running a function backwards — when that is possible, and what it does to the graph.',
    objectives: [
      'Find the inverse of a function algebraically',
      'Recognise when an inverse exists',
      'Relate the graph of an inverse to the original',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Undoing',
        blocks: [
          text('A currency converter turns pounds into euros: multiply by 1.17. Turning euros back into pounds means dividing by 1.17.'),
          text('That second machine is the **inverse** of the first. Everything in this lesson is the question of when a machine can be run backwards, and how to find the rule for doing so.'),
          warning('The notation is unfortunate. f⁻¹(x) does not mean 1 / f(x) — that −1 is not an exponent, and 1/f(x) is a completely different function. It is read "f inverse", and it is the undoing machine.', 'Not a reciprocal'),
        ],
      },
      intuition: {
        title: 'Swapping input and output',
        blocks: [
          text('If f takes 4 to 11, then f⁻¹ takes 11 back to 4. Inputs and outputs exchange roles, which is the whole definition:'),
          math('f^{-1}(f(x)) = x \\quad \\text{and} \\quad f(f^{-1}(x)) = x'),
          text('That gives a method. Write y = f(x), swap x and y, and solve for y again:'),
          steps([
            { label: 'Write the rule.', latex: 'y = 3x + 5' },
            { label: 'Swap x and y — this is the step that inverts.', latex: 'x = 3y + 5' },
            { label: 'Solve for y.', latex: 'y = \\frac{x - 5}{3}' },
          ]),
          warning('The swap is not optional bookkeeping. Rearranging without swapping gives back the same function written differently — the roles of input and output have to actually change places.', 'Swap first, then solve'),
          text('Not every function can be undone. Squaring sends 3 and −3 both to 9, so an inverse asked to undo 9 has no way to choose between them.'),
          insight('A function has an inverse exactly when no output is produced twice — the **horizontal line test**. y = x² fails it, which is why √ is defined only for the positive half: the domain is restricted until the function becomes reversible.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 6,
              lines: [
                { slope: 2, intercept: 3, label: 'f' },
                { slope: 0.5, intercept: -1.5, label: 'f⁻¹' },
                { slope: 1, intercept: 0, label: 'y = x', dashed: true },
              ],
            },
            'A function and its inverse are mirror images in the dashed line y = x. Swapping x and y is a reflection, which is why the picture looks like one.',
          ),
          tip('Every point (a, b) on f becomes (b, a) on f⁻¹. That single fact explains the reflection, the horizontal line test, and why the domain of one is the range of the other.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the inverse of f(x) = (x + 2) / 5, and check it.'),
          steps([
            { label: 'Write y for f(x).', latex: 'y = \\frac{x + 2}{5}' },
            { label: 'Swap x and y.', latex: 'x = \\frac{y + 2}{5}' },
            { label: 'Multiply out.', latex: '5x = y + 2' },
            { label: 'Solve for y.', latex: 'y = 5x - 2' },
            { label: 'Check by composing.', latex: 'f^{-1}(f(3)) = f^{-1}(1) = 3 \\;\\checkmark' },
          ]),
          text('Notice that the operations reversed and their order reversed too: f adds 2 then divides by 5, so f⁻¹ multiplies by 5 then subtracts 2. Undoing a sequence means undoing each step, last one first — the same reason you take your shoes off before your socks.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('inverse-functions', 4)] },
      independent: { title: 'On your own', blocks: [practice('inverse-functions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('inverse-functions', 3, 'Inverses, and when one fails to exist.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'f⁻¹ undoes f; the −1 is not an exponent.',
            'To find it: write y = f(x), swap x and y, solve for y.',
            'The graph of f⁻¹ is the reflection of f in the line y = x.',
            'An inverse exists only when no output repeats — the horizontal line test.',
            'Undo the steps in reverse order.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'logarithms-01',
    unitId: 'unit-logarithms',
    conceptId: 'logarithms',
    title: 'Logarithms',
    summary: 'The question "what power was that?" — asked often enough to deserve its own function.',
    objectives: [
      'Convert between exponential and logarithmic form',
      'Evaluate a logarithm by asking what power is needed',
      'Use the three log laws to combine and split logarithms',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The question with no operation',
        blocks: [
          text('A colony doubles every hour, starting from 100. How long until there are 3200?'),
          math('100 \\cdot 2^{t} = 3200 \\quad\\Longrightarrow\\quad 2^{t} = 32'),
          text('You can answer this one by counting: 2, 4, 8, 16, 32 — five doublings. But if the target were 3000 instead, counting would not get there, and no amount of algebra you currently have will free t from that exponent.'),
          text('Every previous obstacle of this kind was solved by inventing the undoing operation: subtraction undoes addition, division undoes multiplication, roots undo squaring. The exponential needs one too, and this is it.'),
        ],
      },
      intuition: {
        title: 'A logarithm is an exponent',
        blocks: [
          text('Read the notation as a question. log₂(32) asks: **2 to what power gives 32?** The answer is the exponent, 5.'),
          math('\\log_{b}(x) = y \\iff b^{y} = x'),
          insight('The two lines say the same thing in different arrangements, and being able to move between them turns most logarithm problems into exponent problems you have handled since Grade 8.'),
          warning('The base is what gets raised, and the answer is the power. Answering log₂(8) with 2 is answering a different question — read it aloud as "2 to what power gives 8?" and the answer, 3, is unmistakable.', 'Which number is the answer'),
          text('Because a logarithm *is* an exponent, every exponent rule becomes a log law read backwards:'),
          table(['Exponent rule', 'Log law'], [
            ['bᵐ · bⁿ = bᵐ⁺ⁿ', 'log(xy) = log x + log y'],
            ['bᵐ / bⁿ = bᵐ⁻ⁿ', 'log(x/y) = log x − log y'],
            ['(bᵐ)ⁿ = bᵐⁿ', 'log(xⁿ) = n log x'],
          ]),
          warning('The first law turns *multiplication* into addition — nothing turns an addition into anything. log(x + y) does not split up at all, and log(x/y) is a subtraction, not a division of two logs.', 'The two laws that do not exist'),
          text('A logarithm is only defined for positive inputs, because no power of a positive base is ever zero or negative. That is the domain restriction behind every logarithmic graph.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'logarithm',
              min: -4,
              max: 8,
              asymptotes: { vertical: [0] },
              points: [
                { x: 1, y: 0, label: '(1, 0)' },
                { x: 8, y: 3 },
              ],
              label: 'y = log₂ x',
            },
            'The logarithm curve. It crosses the axis at x = 1, because any base to the power 0 is 1, and it never reaches the vertical axis at all.',
          ),
          text('This is the exponential curve reflected in y = x, exactly as the previous lesson would predict — the exponential’s horizontal asymptote has become the logarithm’s vertical one, and its y-intercept at (0, 1) has become the x-intercept at (1, 0).'),
          tip('The curve climbs without limit, but slowly: reaching y = 10 takes an input of 1024. That is why logarithmic scales are used for earthquakes and sound — they compress an enormous range into a readable one.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Write log(x³) − log(x) as a single logarithm, then solve log₃(x + 1) = 4.'),
          steps([
            { label: 'A difference of logs is a quotient.', latex: '\\log\\!\\left(\\frac{x^{3}}{x}\\right)' },
            { label: 'Simplify inside.', latex: '\\log(x^{2})' },
            { label: 'Or use the power law instead.', latex: '2\\log x', note: 'Both forms are correct; either may be asked for.' },
          ]),
          text('Now the equation. Rewrite it in exponential form and the logarithm disappears:'),
          steps([
            { label: 'Read the definition backwards.', latex: '3^{4} = x + 1' },
            { label: 'Evaluate the power.', latex: '81 = x + 1' },
            { label: 'Solve.', latex: 'x = 80' },
            { label: 'Check the input is positive.', latex: 'x + 1 = 81 > 0 \\;\\checkmark' },
          ]),
          insight('That last check is not a formality. Log equations can produce candidates that ask for the logarithm of a negative number, and those must be discarded — the same discipline as the extraneous roots of a radical equation.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('logarithms', 4)] },
      independent: { title: 'On your own', blocks: [practice('logarithms', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('logarithms', 3, 'The log laws, and the two that do not exist.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'log_b(x) asks "b to what power gives x?" — the answer is an exponent.',
            'log_b(x) = y and bʸ = x are the same statement rearranged.',
            'Products become sums, quotients become differences, powers come out in front.',
            'log(x + y) does not split, and log(x/y) is not log x ÷ log y.',
            'Only positive inputs have logarithms — always check.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'unit-circle-01',
    unitId: 'unit-trig-functions',
    conceptId: 'unit-circle',
    title: 'The Unit Circle',
    summary: 'Freeing sine and cosine from the right triangle, so that every angle has them.',
    objectives: [
      'Read sine and cosine as the coordinates of a point on the unit circle',
      'Use reference angles and quadrant signs',
      'Convert between degrees and radians',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'An angle a triangle cannot hold',
        blocks: [
          text('In Geometry, sine and cosine were ratios of sides in a right triangle. That definition is exact, and it runs out almost immediately.'),
          text('What is cos 120°? No right triangle has a 120° angle — the other two would have to add to −30°. Yet a calculator returns −0.5 without complaint, and a Ferris wheel turning past the top has not stopped having a position.'),
          text('The definition needs to grow. Everything true of the triangle version stays true; it simply stops being the whole story.'),
        ],
      },
      intuition: {
        title: 'Coordinates instead of ratios',
        blocks: [
          text('Draw a circle of radius 1 at the origin. Measure an angle θ anticlockwise from the positive x-axis, and mark where the arm meets the circle. That point is the definition:'),
          math('(\\cos\\theta,\\ \\sin\\theta)'),
          insight('For an angle under 90° this is the old definition. The arm, its shadow on the x-axis and the vertical drop form a right triangle with hypotenuse 1 — so cosine is adjacent over 1 and sine is opposite over 1. Nothing has been replaced, only extended.'),
          warning('Cosine is across and sine is up: the point is (cos θ, sin θ), in that order. Alphabetical order matches coordinate order, which is worth holding on to.', 'Which one is which'),
          text('The extension is now free. The arm can sweep past 90°, past 180°, all the way round and round again — it always lands somewhere, so every angle has a sine and a cosine.'),
          text('Beyond the first quadrant, the *size* comes from the **reference angle** — the acute angle to the nearest part of the x-axis — and the *sign* comes from the quadrant, because coordinates are negative to the left and below:'),
          table(['Quadrant', 'cos (across)', 'sin (up)'], [
            ['I (0°–90°)', '+', '+'],
            ['II (90°–180°)', '−', '+'],
            ['III (180°–270°)', '−', '−'],
            ['IV (270°–360°)', '+', '−'],
          ]),
          text('Angles also get a second unit here. Instead of the arbitrary 360 parts of a full turn, measure the angle by the arc length it cuts on a unit circle. The whole circumference is 2π, so:'),
          math('180^{\\circ} = \\pi \\text{ radians}'),
          tip('Convert by multiplying by π/180 to go to radians, and by 180/π to come back. Radians look awkward for a fortnight and then become the only sensible unit — every formula in calculus assumes them.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'unit-circle', angle: 120, showCoordinates: true, showQuadrantLabels: true, marks: [0, 30, 45, 60, 90, 135, 180, 225, 270, 315] },
            'At 120° the point sits in the second quadrant, at (−1/2, √3/2). Its reference angle is 60°, whose values are 1/2 and √3/2 — the same numbers, with the x-coordinate now negative because the point is left of the axis.',
          ),
          text('This is why cos 120° = −0.5 without any triangle being involved. The point is half a unit to the left of the vertical axis, and that is all cosine has ever measured.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find sin 210° and cos 210° exactly.'),
          steps([
            { label: 'Locate the quadrant.', latex: '180^{\\circ} < 210^{\\circ} < 270^{\\circ}', note: 'Third quadrant: left and below, so both coordinates are negative.' },
            { label: 'Find the reference angle.', latex: '210^{\\circ} - 180^{\\circ} = 30^{\\circ}' },
            { label: 'Recall the size from the 30° values.', latex: '\\tfrac{1}{2} \\text{ and } \\tfrac{\\sqrt{3}}{2}' },
            { label: 'Apply the quadrant signs.', latex: '\\sin 210^{\\circ} = -\\tfrac{1}{2}, \\quad \\cos 210^{\\circ} = -\\tfrac{\\sqrt{3}}{2}' },
          ]),
          text('Two questions, always in the same order: how big, and which sign. The reference angle answers the first and the quadrant answers the second, and mixing them up is the one error worth guarding against.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('unit-circle', 4)] },
      independent: { title: 'On your own', blocks: [practice('unit-circle', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('unit-circle', 3, 'Signs, reference angles and radians together.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The point on the unit circle at angle θ is (cos θ, sin θ) — across, then up.',
            'Every angle has a sine and a cosine, including obtuse and negative ones.',
            'The reference angle gives the size; the quadrant gives the sign.',
            '180° = π radians; multiply by π/180 one way and 180/π the other.',
            'The right-triangle definition is still true — it is the first quadrant of this one.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'trig-graphs-01',
    unitId: 'unit-trig-functions',
    conceptId: 'trig-graphs',
    title: 'Graphs of Sine and Cosine',
    summary: 'What a circle looks like when you unroll it — and why so much of the world is shaped like this.',
    objectives: [
      'Sketch the sine and cosine curves',
      'Find the amplitude and period of a transformed wave',
      'Find the phase shift, dividing the constant inside by the coefficient of x',
      'Model a repeating quantity with a trigonometric function',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Unrolling the circle',
        blocks: [
          text('Walk anticlockwise round the unit circle, and plot your height against the distance travelled.'),
          text('At the start the height is 0. It climbs to 1 a quarter of the way round, falls back through 0 at the halfway point, drops to −1, and returns. Keep walking and it does exactly the same thing again, for ever.'),
          text('That plot is the sine curve, and everything about it is a consequence of the circle. Its height never exceeds 1 because the radius is 1; it repeats every 2π because that is once round.'),
        ],
      },
      intuition: {
        title: 'The same transformations, on a wave',
        blocks: [
          text('Sine and cosine are the same curve a quarter-turn apart. Cosine starts at its maximum, because at angle 0 the point is at (1, 0) and cosine reads the across-coordinate.'),
          text('Two numbers describe any such wave:'),
          list([
            '**Amplitude** — how far it rises above the middle line, measured from the middle to the peak.',
            '**Period** — how far along the input axis before the pattern repeats.',
          ]),
          math('y = a\\sin(bx) \\quad\\Longrightarrow\\quad \\text{amplitude } |a|, \\quad \\text{period } \\frac{2\\pi}{b}'),
          insight('Both come straight from the previous lessons and neither is a new rule. a is an outside change, so it stretches vertically — that is the amplitude. b is an inside change, so it squashes horizontally, and the period shortens.'),
          warning('The period is 2π/b, not b. A larger number inside drives the input round the circle faster, so the wave gets *shorter*: y = sin(2x) completes two full waves in the space the plain sine takes for one.', 'The coefficient is not the period'),
          warning('Amplitude is measured from the middle line to the peak — half the total rise and fall. A wave running between −4 and 4 has amplitude 4, not 8.', 'Half the height, not all of it'),
          text('Adding k outside lifts the middle line, which is how a wave that oscillates between 8 and 12 is written: amplitude 2 about a middle line of 10.'),
          text('A fourth number slides the whole wave sideways. It is called the **phase shift**, and it is the one place where the transformation rules need care:'),
          math('y = a\\sin\\big(b(x - c)\\big) + k \\quad\\Longrightarrow\\quad \\text{shifted } c \\text{ to the right}'),
          warning('Written the other way — y = a sin(bx − c) — the shift is *not* c. Factor the b out first: sin(2x − π) is sin(2(x − π/2)), so the wave moves π/2, not π. Whatever is done to x inside is done to the shift as well.', 'Factor before you read the shift'),
          insight('This is not a new rule either. It is the same "inside changes go the opposite way and are divided by the coefficient" already met on parabolas — which is why the course spent a lesson on transformations before coming anywhere near a wave.'),
          text('Sine and cosine are themselves a phase shift apart: cos x is sin(x + π/2), the sine wave dragged a quarter-period to the left. Any wave can be written with either function, and the phase shift is what changes between the two versions.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'sine',
              min: -7,
              max: 7,
              points: [
                { x: 1.5708, y: 1, label: 'peak' },
                { x: 3.1416, y: 0 },
              ],
              label: 'y = sin x',
            },
            'One full wave every 2π ≈ 6.28 units, running between −1 and 1. The zeros are at every multiple of π, where the point on the circle is on the horizontal axis.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'sine',
              transform: { a: 3, b: 2 },
              showParent: true,
              min: -7,
              max: 7,
              label: 'y = 3 sin 2x',
            },
            'The same wave with amplitude 3 and period π. Trebling outside made it taller; doubling inside made it twice as quick, so half as long.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'sine',
              transform: { b: 2, h: 0.7854 },
              showParent: true,
              min: -7,
              max: 7,
              label: 'y = sin(2x − π/2)',
            },
            'The same period, slid right by π/4 — which is π/2 divided by the 2 inside, and not π/2. The dashed curve behind is where it was before the shift.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A tide runs between 2 m and 8 m, and repeats every 12 hours. Write a model for the depth d at time t hours, starting at mean depth and rising.'),
          steps([
            { label: 'The middle line is halfway between the extremes.', latex: 'k = \\frac{2 + 8}{2} = 5' },
            { label: 'The amplitude is the distance from middle to peak.', latex: 'a = 8 - 5 = 3' },
            { label: 'Choose b from the period.', latex: '\\frac{2\\pi}{b} = 12 \\;\\Rightarrow\\; b = \\frac{\\pi}{6}' },
            { label: 'Starting at the middle and rising means sine, not cosine.', latex: 'd = 3\\sin\\!\\left(\\frac{\\pi}{6}t\\right) + 5' },
          ]),
          text('Test it. At t = 0 the depth is 5 m, the mean. At t = 3 it is 3 sin(π/2) + 5 = 8 m, high tide, a quarter of the period in. At t = 12 it is back to 5 and rising. The model behaves like a tide because it was built from what the tide does.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('trig-graphs', 4)] },
      independent: { title: 'On your own', blocks: [practice('trig-graphs', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('trig-graphs', 3, 'Amplitude, period and modelling together.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Sine and cosine are the unit circle unrolled; cosine starts at its maximum.',
            'Amplitude is measured from the middle line to the peak — half the total height.',
            'For y = a sin(bx), the amplitude is |a| and the period is 2π/b.',
            'A larger coefficient inside makes the wave shorter, not longer.',
            'The phase shift of y = a sin(bx − c) is c ÷ b, not c — factor the b out before reading it.',
            'Anything that repeats — tides, sound, daylight, current — can be modelled this way.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'function-operations-01',
    unitId: 'unit-function-shapes',
    conceptId: 'function-operations',
    title: 'Combining and Composing Functions',
    summary: 'Adding, multiplying and — the one that is genuinely new — feeding one function into another.',
    objectives: [
      'Evaluate sums, differences and products of two functions',
      'Evaluate and build a composition f(g(x))',
      'Explain why f(g(x)) and g(f(x)) are usually different functions',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Four ways to combine, and only one is new',
        blocks: [
          text('Given two functions, there are obvious things to do with them. Add their outputs. Subtract them. Multiply them. All three are exactly what they sound like:'),
          math('(f + g)(x) = f(x) + g(x)'),
          text('Nothing has happened there except notation — the same input goes into both machines, and the two outputs are combined afterwards.'),
          insight('The fourth way is different in kind. Instead of combining two outputs, run the machines *in series*: the output of one becomes the input of the other. That is composition, and it is the operation the rest of Algebra II is built on.'),
        ],
      },
      intuition: {
        title: 'One machine after another',
        blocks: [
          text('Composition is written f(g(x)), or sometimes (f ∘ g)(x). Both mean the same thing:'),
          list([
            'g runs first. It is the one inside the brackets.',
            'Whatever g produces is handed to f.',
            'What f produces is the answer.',
          ], true),
          text('With f(x) = 2x + 1 and g(x) = x²:'),
          math('f(g(3)) = f(9) = 19 \\qquad g(f(3)) = g(7) = 49'),
          warning('19 and 49. The order is not a formality — squaring then doubling is a different operation from doubling then squaring, and f(g(x)) and g(f(x)) are different functions. Work from the inside out, exactly as you would with nested brackets in arithmetic.', 'Order changes the answer'),
          text('To build the rule rather than a single value, substitute the whole of g wherever f has an x:'),
          math('f(g(x)) = 2(x^{2}) + 1 = 2x^{2} + 1'),
          warning('The circle in f ∘ g is not a multiplication sign. f(g(x)) is one machine feeding another; f(x)·g(x) is two separate answers multiplied. With the functions above they are 2x² + 1 and 2x³ + x² — not the same function at any x but a couple of coincidences.', 'Composition is not multiplication'),
          insight('Composition is what makes an inverse definable. f⁻¹ is the function that undoes f, and "undoes" means precisely f(f⁻¹(x)) = x. Without composition you can only check an inverse by drawing it and looking.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Composition is a production line. The number goes in at one end and is handled twice:'),
          table(['Stage', 'f(g(3)) with g first', 'g(f(3)) with f first'], [
            ['input', '3', '3'],
            ['first machine', 'g: square it → 9', 'f: double and add 1 → 7'],
            ['second machine', 'f: double and add 1 → 19', 'g: square it → 49'],
            ['output', '19', '49'],
          ]),
          tip('The bracket nesting tells you the order of the line, and it reads inside-out — the same convention as 2(3 + 4), where the addition happens first because it is deepest.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('With f(x) = 3x − 4 and g(x) = 2x + 5, find f(g(x)) and (f · g)(2).'),
          steps([
            { label: 'For f(g(x)), substitute all of g into f.', latex: 'f(g(x)) = 3(2x + 5) - 4' },
            { label: 'Expand and collect.', latex: '6x + 15 - 4 = 6x + 11' },
            { label: 'For the product, evaluate each function at 2.', latex: 'f(2) = 2, \\quad g(2) = 9' },
            { label: 'Then multiply the outputs.', latex: '2 \\times 9 = 18' },
          ]),
          insight('Check the composition against a value: g(1) = 7 and f(7) = 17, and the rule 6x + 11 gives 17 at x = 1. One substitution is usually enough to catch an expansion error.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('function-operations', 4)] },
      independent: { title: 'On your own', blocks: [practice('function-operations', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('function-operations', 3, 'Compositions involving a quadratic, where the order shows plainly.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            '(f + g)(x), (f − g)(x) and (f · g)(x) put the same input into both and combine the outputs.',
            'f(g(x)) runs g first — it is the function inside the brackets.',
            'f(g(x)) and g(f(x)) are usually different functions. The order is part of the answer.',
            'To build the rule, substitute the entire inner function wherever the outer one has an x.',
            'The circle in f ∘ g is not a times sign.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'piecewise-functions-01',
    unitId: 'unit-function-shapes',
    conceptId: 'piecewise-functions',
    title: 'Piecewise Functions',
    summary: 'One function with more than one rule, and the endpoint that decides which rule owns the join.',
    objectives: [
      'Evaluate a piecewise function by first choosing the branch its condition allows',
      'Decide which branch owns the value where the definition splits',
      'Recognise absolute value and step functions as piecewise definitions',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'A rule that changes partway along',
        blocks: [
          text('Post a parcel and the price does not grow smoothly with weight. It sits at one figure up to 2 kg, jumps, sits at another up to 5 kg, jumps again. There is no single formula, and there is nothing wrong with the situation: the cost is a perfectly definite function of the weight.'),
          text('Mathematics writes such a thing with a brace:'),
          math('f(x) = \\begin{cases} 2x + 1 & x < 3 \\\\ 10 - x & x \\ge 3 \\end{cases}'),
          insight('This is still one function, not two. Every input has exactly one output, which is the whole requirement — the conditions on the right make sure of it by handing each x to precisely one rule.'),
        ],
      },
      intuition: {
        title: 'Read the condition before you substitute',
        blocks: [
          text('Evaluating is a two-step job, and the first step is not arithmetic:'),
          list([
            'Look at the input and find which condition it satisfies.',
            'Substitute into that rule, and ignore the other one entirely.',
          ], true),
          text('For the function above, f(1) uses the first rule because 1 < 3, giving 3. And f(5) uses the second, giving 5.'),
          warning('The two rules are not alternatives to choose between, and they are not a rule with an exception. The input decides, and the decision is made before any substituting happens.', 'The condition comes first'),
          text('Everything interesting happens at the joining value. Here the conditions are x < 3 and x ≥ 3, so:'),
          table(['Value', 'x < 3 ?', 'x ≥ 3 ?', 'Rule used'], [
            ['2.9', 'yes', 'no', '2x + 1'],
            ['3', 'no', 'yes', '10 − x'],
            ['3.1', 'no', 'yes', '10 − x'],
          ]),
          insight('Exactly one column says yes on every row, and that is not an accident of this example — it is what makes a piecewise definition legal. If both said yes at x = 3 the function would claim two outputs for one input; if neither did, f(3) would not exist.'),
          text('Two familiar functions turn out to have been piecewise all along. Absolute value is the clearest:'),
          math('|x| = \\begin{cases} -x & x < 0 \\\\ x & x \\ge 0 \\end{cases}'),
          text('The minus sign on the first branch is not making the answer negative. For x < 0, x is already negative, so −x is positive — which is what an absolute value has to be.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'piecewise-graph',
              pieces: [
                { slope: 2, intercept: 1, from: -4, to: 3, openEnd: true },
                { slope: -1, intercept: 10, from: 3, to: 6 },
              ],
              min: -4,
              max: 8,
            },
            'The two rules above, drawn together. At x = 3 one piece stops with a hollow circle — it approaches that point without reaching it — and the other starts with a filled one. The filled dot is the value of f(3).',
          ),
          tip('The hollow-versus-filled circle is the entire visible difference between < and ≤, which is why it is drawn rather than described. Where two branches meet at the same height, the graph is unbroken and the dots vanish into each other.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A car park charges £3 for up to and including 2 hours, £5 for more than 2 and up to 4 hours, and £9 beyond that. What does a stay of exactly 2 hours cost, and a stay of 3 hours?'),
          steps([
            { label: 'Write it as a function.', latex: 'C(t) = \\begin{cases} 3 & t \\le 2 \\\\ 5 & 2 < t \\le 4 \\\\ 9 & t > 4 \\end{cases}' },
            { label: 'For t = 2, which condition holds?', latex: '2 \\le 2', note: 'The first band, because it says "up to and including".' },
            { label: 'So a two-hour stay costs £3.', latex: 'C(2) = 3' },
            { label: 'For t = 3, the middle band holds.', latex: '2 < 3 \\le 4' },
            { label: 'A three-hour stay costs £5.', latex: 'C(3) = 5' },
          ]),
          insight('The boundary is where the money is. "Up to and including 2 hours" and "less than 2 hours" differ for exactly one input — and that one input is the case that ends up in a complaint. In mathematics as on the sign, the whole content of the rule is in the small word.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('piecewise-functions', 4)] },
      independent: { title: 'On your own', blocks: [practice('piecewise-functions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('piecewise-functions', 3, 'Values exactly at the join, where the sign decides.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A piecewise function is one function whose rule depends on the input.',
            'Check the condition first, then substitute into that branch only.',
            'At the joining value, the branch whose sign has a line under it — ≤ or ≥ — is the one that owns it.',
            'A hollow circle means the graph approaches the point without reaching it; a filled one means it is the value.',
            'Absolute value and step functions are piecewise definitions in disguise.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'exponential-equations-01',
    unitId: 'unit-logarithms',
    conceptId: 'exponential-equations',
    title: 'Exponential and Logarithmic Equations',
    summary: 'Getting the unknown down out of the exponent — the job logarithms were invented to do.',
    objectives: [
      'Solve an exponential equation by matching bases where that is possible',
      'Solve one with a logarithm where it is not, including a change of base',
      'Check the solutions of a logarithmic equation against the domain',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The unknown is upstairs',
        blocks: [
          text('Every equation you have solved has had the unknown on the ground floor, where the ordinary operations can reach it. Not this one:'),
          math('3^{x} = 20'),
          text('Dividing by 3 does nothing useful, because 3^x is not 3 times x. Subtracting does nothing. There is no root to take, since it is not the base that is unknown.'),
          insight('This is the equation logarithms exist for. Everything in the previous lesson — the definition, the three laws — was preparation for being able to answer this in one line.'),
        ],
      },
      intuition: {
        title: 'Match the bases, or take logs',
        blocks: [
          text('The first thing to try is always the easier one: can both sides be written as powers of the same base?'),
          math('2^{3x - 1} = 32 = 2^{5} \\;\\longrightarrow\\; 3x - 1 = 5 \\;\\longrightarrow\\; x = 2'),
          text('If two powers of the same base are equal, their exponents are equal. No logarithm needed, and no calculator either.'),
          text('When the bases refuse to match — 3^x = 20 is not going to become a power of 3 — take the logarithm of **both sides** and use the law that brings an exponent down in front:'),
          math('\\log(3^{x}) = \\log 20 \\;\\longrightarrow\\; x \\log 3 = \\log 20 \\;\\longrightarrow\\; x = \\frac{\\log 20}{\\log 3} \\approx 2.73'),
          warning('A logarithm is an operation, so it goes on both sides like every other operation. Writing log(3^x) = 20 changes the equation into a different one.', 'Both sides, always'),
          text('That last fraction is also the change-of-base rule, which is how any logarithm is evaluated on a calculator that only knows two of them:'),
          math('\\log_{b} a = \\frac{\\log a}{\\log b}'),
          warning('The number you are taking the logarithm *of* goes on top. Test any version you write down against something you know: log₂ 8 must come out as 3, so it is log 8 ÷ log 2 and not the other way up.', 'Argument on top'),
          text('Logarithmic equations run the same machinery backwards. Rewrite the logarithm as a power:'),
          math('\\log_{5}(x + 3) = 2 \\;\\longrightarrow\\; x + 3 = 5^{2} \\;\\longrightarrow\\; x = 22'),
          insight('And they come with a domain, so they come with a check. A logarithm accepts only positive arguments, so any candidate that makes something inside a log zero or negative is discarded — the same discipline as radical and rational equations, for the same reason.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'exponential',
              transform: { b: 1 },
              lines: [{ slope: 0, intercept: 8, dashed: true }],
              points: [{ x: 3, y: 8, label: '2^x = 8' }],
              min: -2,
              max: 10,
            },
            'y = 2^x with the line y = 8. Solving an exponential equation is asking where the curve reaches a given height, and the exponential climbs past every height exactly once — which is why these equations have exactly one solution.',
          ),
          tip('The curve is above the axis everywhere, so 2^x = −8 has no solution at all, and no amount of algebra will produce one. Checking whether the right-hand side is even reachable takes a second and saves a page.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A colony of 400 bacteria grows by 6% an hour. When does it reach 1200?'),
          steps([
            { label: 'Write the model.', latex: '400 \\cdot 1.06^{t} = 1200' },
            { label: 'Divide by the starting amount.', latex: '1.06^{t} = 3' },
            { label: 'Take logs of both sides.', latex: 't \\log 1.06 = \\log 3' },
            { label: 'Divide.', latex: 't = \\frac{\\log 3}{\\log 1.06} \\approx 18.9' },
          ]),
          text('So a little under nineteen hours. Note what was *not* done: the growth was not treated as 6% of 400 each hour. That would be 24 bacteria an hour, reaching 1200 in about 33 hours — a wrong answer of the kind that looks reasonable.'),
          insight('Dividing by the starting amount first is worth the habit. It clears everything that is not the unknown out of the way, leaving one power to undo, and the same three lines finish every growth, decay and half-life question there is.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('exponential-equations', 4)] },
      independent: { title: 'On your own', blocks: [practice('exponential-equations', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('exponential-equations', 3, 'Log equations needing a domain check, and growth over time.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Try matching the bases first — if the bases agree, the exponents do.',
            'Otherwise take the logarithm of both sides and bring the exponent down in front.',
            'Change of base: log_b a = log a ÷ log b, with the argument on top.',
            'To solve a logarithmic equation, rewrite it as a power.',
            'Check every candidate: a logarithm of zero or of a negative number does not exist.',
          ]),
        ],
      },
    },
  }),
];
